<template>
  <div class="user-container">
    <template v-if="!bindingChecked">
      <div class="loading">检查绑定状态...</div>
    </template>
    
    <template v-else-if="showVerificationCode">
      <div class="verification-box">
        <h3>游戏ID绑定验证码</h3>
        <div class="code-display">
          <span class="code">{{ verificationCode }}</span>
          <button @click="copyCode" class="copy-btn">复制</button>
        </div>
        <p>验证码将在 {{ formattedExpiryTime }} 分钟后过期</p>
        <button @click="getNewCode" class="refresh-btn">重新获取验证码</button>
      </div>
    </template>

    <template v-else>
      <div class="profile-container">
        <h2>个人信息管理</h2>
        
        <div v-if="isLoading" class="loading-stats">
          <i class="el-icon-loading"></i>
          正在加载游戏数据...
        </div>

        <div v-else-if="errorMsg" class="error-msg">
          {{ errorMsg }}
          <el-button @click="fetchUserStats" size="small">重试</el-button>
        </div>

        <div v-else class="game-stats">
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="stat-item">
                <label>游戏ID</label>
                <div class="value">{{ userStats.UserName }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <label>持有金币</label>
                <div class="value">{{ userStats.TotalCoin }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <label>总对局数</label>
                <div class="value">{{ userStats.TotalGames }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-item">
                <label>胜率</label>
                <div class="value">
                  {{ ((userStats.Wins / userStats.TotalGames) * 100 || 0).toFixed(1) }}%
                </div>
              </div>
            </el-col>
          </el-row>

          <el-divider></el-divider>

          <el-row :gutter="20">
            <el-col :span="12">
              <div class="win-loss">
                <div class="win">
                  <i class="el-icon-success"></i>
                  胜场：{{ userStats.Wins }}
                </div>
                <div class="loss">
                  <i class="el-icon-error"></i>
                  负场：{{ userStats.Losses }}
                </div>
              </div>
            </el-col>
          </el-row>

          <!-- 守护颜色模块 -->
          <div v-if="guardLevel > 0" class="guard-color-section">
            <el-divider></el-divider>
            <h3>游戏内颜色设置</h3>
            
            <div v-if="isGuardLoading" class="loading-stats">
              <i class="el-icon-loading"></i>
              正在加载颜色设置...
            </div>
            
            <div v-else class="color-controls">
              <!-- 颜色状态控制 -->
              <div class="color-status">
                <el-button 
                  :type="guardColorInfo.IsActive ? 'success' : 'danger'" 
                  @click="toggleGuardColorActive"
                  size="small">
                  {{ guardColorInfo.IsActive ? '颜色功能已开启' : '颜色功能已关闭' }}
                </el-button>
                <span class="status-text">当前状态：{{ guardColorInfo.IsActive ? '开启' : '关闭' }}</span>
              </div>

              <!-- 颜色选择区域 -->
              <div class="color-selection">
                <h4>当前颜色</h4>
                <div class="current-color-section">
                  <div 
                    class="current-color-display"
                    :style="{ backgroundColor: selectedColor }"
                    @click="openColorPicker">
                    <span class="color-value">{{ selectedColor }}</span>
                  </div>
                  <el-button 
                    @click="openColorPicker" 
                    size="small"
                    type="primary">
                    选择颜色
                  </el-button>
                </div>
              </div>

              <!-- 颜色选择器模态框 -->
              <el-dialog 
                title="选择颜色" 
                :visible.sync="showColorPicker" 
                width="360px"
                :before-close="closeColorPicker">
                <div class="color-picker-modal">
                  <!-- 常用颜色 -->
                  <div class="common-colors-section">
                    <p class="section-label">常用颜色</p>
                    <div class="common-colors-wrap">
                      <span 
                        v-for="color in commonColors" 
                        :key="color"
                        class="common-color-swatch"
                        :class="{ 'selected': selectedColor === color }"
                        :style="{ backgroundColor: color }"
                        @click="selectColor(color); closeColorPicker()"
                        :title="color">
                      </span>
                    </div>
                  </div>
                  
                  <!-- 自定义颜色 -->
                  <div class="custom-color-section">
                    <p class="section-label">自定义颜色</p>
                    <div class="custom-color-wrap">
                      <el-color-picker 
                        v-model="selectedColor" 
                        size="medium"
                        @change="selectColor(selectedColor)">
                      </el-color-picker>
                      <span class="color-hex-label">{{ selectedColor }}</span>
                    </div>
                  </div>
                </div>
              </el-dialog>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      bindingChecked: false,
      showVerificationCode: false,
      verificationCode: '',
      expiryMinutes: 0,
      pollInterval: null,
      // 新增数据项
      userStats: null,
      isLoading: false,
      errorMsg: '',
      // 守护颜色相关数据
      guardLevel: 0,
      guardColorInfo: {
        Color: 0,
        IsActive: false
      },
      isGuardLoading: false,
      showColorPicker: false,
      selectedColor: '#409eff',
      commonColors: [
        '#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399',
        '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff',
        '#00ffff', '#ffa500', '#800080', '#ffc0cb', '#a52a2a'
      ]
    };
  },
  computed: {
    formattedExpiryTime() {
      //return Math.floor(this.expiryMinutes * 60); // 转换为秒显示
      return this.expiryMinutes
    }
  },
  mounted() {
    this.checkBindingStatus();
    this.fetchGuardStatus();
  },
  beforeDestroy() {
    clearInterval(this.pollInterval);
  },
  methods: {
    async checkBindingStatus() {
      try {
        const response = await axios.get('/api/Bind/check-binding');
        if (!response.data.response) {
          this.getVerificationCode();
        } else {
          this.bindingChecked = true;
          await this.fetchUserStats();
        }
      } catch (error) {
        console.error('检查绑定失败:', error);
      }
    },

    async getVerificationCode() {
      try {
        const response = await axios.get('/api/Bind/generate-code');
        this.verificationCode = 'BIND-' + response.data.Code;
        this.expiryMinutes = response.data.ExpiryMinutes;
        this.showVerificationCode = true;
        this.bindingChecked = true;
        
        // 启动轮询
        this.pollInterval = setInterval(() => {
          this.checkPollingStatus();
        }, 5000); // 每5秒检查一次

      } catch (error) {
        console.error('获取验证码失败:', error);
      }
    },

    async checkPollingStatus() {
      try {
        const response = await axios.get('/api/Bind/check-binding');
        if (response.data.response) {
          clearInterval(this.pollInterval);
          this.showVerificationCode = false;
          // 这里可以添加绑定成功后的数据刷新逻辑
          // 新增：获取玩家数据
          await this.fetchUserStats();
        }
      } catch (error) {
        console.error('轮询检查失败:', error);
      }
    },

    copyCode() {
      navigator.clipboard.writeText(this.verificationCode)
        .then(() => {
          this.$message.success('验证码已复制到剪贴板');
        })
        .catch(err => {
          console.error('复制失败:', err);
        });
    },
    
      // 新增方法：获取玩家数据
    async fetchUserStats() {
      try {
        this.isLoading = true;
        const user = JSON.parse(localStorage.getItem('user'));
        const response = await axios.get(`/api/PlayerStats/GetStats/${user.uID}`);
        console.log(user)
        if (response.data.success) {
          console.log(response.data)
          this.userStats = response.data.response;
        } else {
          this.errorMsg = '数据获取失败，请稍后重试';
        }
      } catch (error) {
        console.error('获取玩家数据失败:', error);
        this.errorMsg = '连接服务器失败';
      } finally {
        this.isLoading = false;
      }
    },

    async getNewCode() {
      clearInterval(this.pollInterval);
      await this.getVerificationCode();
    },

    // 守护颜色相关方法
    async fetchGuardStatus() {
      try {
        this.isGuardLoading = true;
        const response = await axios.get('/api/UserGuard/GetUserGuards');
        if (response.data && response.data.success) {
          this.guardLevel = response.data.response;
          if (this.guardLevel > 0) {
            await this.fetchGuardColorInfo();
          }
        } else {
          console.warn('获取守护状态失败:', response.data && response.data.msg);
        }
      } catch (error) {
        console.error('获取守护状态失败:', error);
      } finally {
        this.isGuardLoading = false;
      }
    },

    async fetchGuardColorInfo() {
      try {
        const response = await axios.get('/api/UserGuard/GetUserGuardColorInfo');
        if (response.data && response.data.success) {
          this.guardColorInfo = response.data.response;
          // 将RGB格式转换为HEX格式用于显示
          this.selectedColor = this.rgbToHex(this.guardColorInfo.Color);
        } else {
          console.warn('获取守护颜色信息失败:', response.data && response.data.msg);
        }
      } catch (error) {
        console.error('获取守护颜色信息失败:', error);
      }
    },

    async setGuardColor(colorValue) {
      try {
        console.log('设置守护颜色:', colorValue);
        const response = await axios.post(`/api/UserGuard/SetUserGuardColor?color=${colorValue}`);
        if (response.data && response.data.success) {
          this.$message.success('颜色设置成功');
          this.guardColorInfo.Color = colorValue;
        } else {
          const errorMsg = (response.data && response.data.msg) || '设置失败';
          this.$message.error(`颜色设置失败: ${errorMsg}`);
        }
      } catch (error) {
        console.error('设置守护颜色失败:', error);
        const errorMsg = (error.response && error.response.data && error.response.data.msg) || '网络错误';
        this.$message.error(`颜色设置失败: ${errorMsg}`);
      }
    },

    async toggleGuardColorActive() {
      try {
        const response = await axios.post('/api/UserGuard/ToggleUserGuardColorActive');
        if (response.data && response.data.success) {
          this.guardColorInfo.IsActive = response.data.response;
          const statusText = this.guardColorInfo.IsActive ? '开启' : '关闭';
          this.$message.success(`颜色功能已${statusText}`);
        } else {
          const errorMsg = (response.data && response.data.msg) || '操作失败';
          this.$message.error(`切换状态失败: ${errorMsg}`);
        }
      } catch (error) {
        console.error('切换颜色状态失败:', error);
        const errorMsg = (error.response && error.response.data && error.response.data.msg) || '网络错误';
        this.$message.error(`切换状态失败: ${errorMsg}`);
      }
    },

    // RGB转HEX格式
    rgbToHex(rgb) {
      if (!rgb || rgb === 0) return '#409eff';
      const hex = rgb.toString(16).padStart(6, '0');
      // RGB格式：RRGGBB
      return '#' + hex;
    },

    // HEX转RGB格式
    hexToRgb(hex) {
      if (!hex || hex === '#') return 0;
      // 移除#号，补齐6位
      const cleanHex = hex.replace('#', '').padStart(6, '0');
      // 转换为RGB十进制值
      const rgb = parseInt(cleanHex, 16);
      console.log('HEX转RGB:', hex, '->', rgb);
      return rgb;
    },

    // 选择颜色
    selectColor(color) {
      this.selectedColor = color;
      const rgbColor = this.hexToRgb(color);
      this.setGuardColor(rgbColor);
    },

    // 打开颜色选择器
    openColorPicker() {
      this.showColorPicker = true;
    },

    // 关闭颜色选择器
    closeColorPicker() {
      this.showColorPicker = false;
    }
  }
};
</script>

<style scoped>
.verification-box {
  padding: 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f5f7fa;
}

.code-display {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 15px 0;
}

.code {
  font-size: 24px;
  letter-spacing: 2px;
  color: #409eff;
}

.copy-btn, .refresh-btn {
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.copy-btn {
  background: #409eff;
  color: white;
}

.refresh-btn {
  background: #67c23a;
  color: white;
  margin-top: 10px;
}

.loading {
  color: #909399;
  font-style: italic;
}
.profile-container {
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* 统计项通用样式 */
.stat-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
  transition: transform 0.2s;
}

.stat-item:hover {
  transform: translateY(-2px);
}

.stat-item label {
  display: block;
  color: #6c757d;
  font-size: 13px;
  margin-bottom: 6px;
}

.stat-item .value {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
}

/* 胜败统计 */
.win-loss div {
  padding: 12px;
  border-radius: 8px;
  margin: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.win i, .loss i {
  font-size: 18px;
}

/* 响应式布局 */
@media (max-width: 768px) {
  .el-col {
    width: 100% !important;
    margin-bottom: 12px;
  }

  .el-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .stat-item {
    margin-bottom: 12px;
  }

  .stat-item .value {
    font-size: 18px;
  }

  .win-loss div {
    font-size: 14px;
  }

  h2 {
    font-size: 20px;
    margin-bottom: 16px;
  }
}

@media (min-width: 769px) {
  .el-col {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .stat-item {
    min-height: 120px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

/* 加载状态优化 */
.loading-stats {
  padding: 30px;
  font-size: 14px;
}

.loading-stats i {
  display: block;
  margin-bottom: 10px;
  font-size: 24px;
}

/* 错误信息优化 */
.error-msg {
  font-size: 14px;
  padding: 16px;
  background: #fff5f5;
  border-radius: 8px;
}

/* 按钮响应式 */
.el-button {
  padding: 8px 16px;
  font-size: 14px;
}

@media (max-width: 480px) {
  .copy-btn, .refresh-btn {
    width: 100%;
    padding: 12px;
    font-size: 15px;
  }

  .code-display {
    flex-direction: column;
    gap: 12px;
  }

  .code {
    font-size: 20px;
  }
}

/* 通用间距优化 */
.el-divider {
  margin: 20px 0;
}

.el-row {
  margin-bottom: 16px;
}

/* 守护颜色模块样式 */
.guard-color-section {
  margin-top: 20px;
}

.guard-color-section h3 {
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 18px;
}

.guard-color-section h4 {
  color: #5a6c7d;
  margin: 16px 0 12px 0;
  font-size: 15px;
}

.color-status {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.status-text {
  color: #6c757d;
  font-size: 14px;
}

.color-selection {
  margin-bottom: 20px;
}

.current-color-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.current-color-display {
  width: 60px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.current-color-display:hover {
  border-color: #409eff;
}

.color-value {
  font-size: 12px;
  color: #fff;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
  font-weight: bold;
}

.color-picker-modal {
  padding: 5px 0;
}

.section-label {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
}

.common-colors-section {
  margin-bottom: 24px;
}

.common-colors-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.common-color-swatch {
  display: inline-block;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid #ebeef5;
  transition: all 0.2s;
}

.common-color-swatch:hover {
  transform: scale(1.1);
  border-color: #409eff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
}

.common-color-swatch.selected {
  border-color: #409eff;
  transform: scale(1.1);
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.custom-color-section {
  margin-bottom: 10px;
}

.custom-color-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-hex-label {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  color: #606266;
  background: #f5f7fa;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  letter-spacing: 1px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .common-color-swatch {
    width: 32px;
    height: 32px;
  }
  
  .color-status {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .current-color-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .current-color-display {
    width: 100%;
    max-width: 200px;
  }
  
  .custom-color-wrap {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .common-color-swatch {
    width: 28px;
    height: 28px;
  }
  
  .guard-color-section h3 {
    font-size: 16px;
  }
  
  .guard-color-section h4 {
    font-size: 14px;
  }
  
  .el-dialog {
    width: 90% !important;
    max-width: 320px;
  }
}
</style>